import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from 'react-markdown';
import Dialog from "./Dialog";
import { useToast } from "../providers/ToastProvider";
import api from "../../services/api";

interface TaskResolutionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    id: string;
    taskId: string;
    userId: string;
    studentName: string;
    taskTitle: string;
    contentUrl?: string;
    status?: string;
  } | null;
  onSuccess: () => void;
}

interface SubmissionData {
  title?: string;
  description?: string;
  contentUrl?: string; // Deliverable Link
  evidenceUrl?: string; // Visual Proof (Screenshot)
  contentBody?: string;
}

export default function TaskResolutionDialog({ isOpen, onClose, item, onSuccess }: TaskResolutionDialogProps) {
  const { showToast } = useToast();
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Image Preview Modal (Cinema Mode)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [zoomScale, setZoomScale] = useState(1);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && item) {
      fetchSubmission();
    }
  }, [isOpen, item]);

  const fetchSubmission = async () => {
    if (!item) return;
    setIsLoading(true);
    try {
      const res = await api.get(`/submissions/single/${item.taskId}/${item.userId}`);
      setSubmission(res.data);
    } catch (err) {
      console.error("Failed to fetch submission:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolve = async (status: "APPROVED" | "REJECTED") => {
    if (!item) return;
    setIsSubmitting(true);
    try {
      await api.patch(`/submissions/status`, {
        taskId: item.taskId,
        userId: item.userId,
        status,
        feedback
      });

      showToast(
        status === "APPROVED" ? "Task Approved" : "Changes Requested",
        `Task status updated for ${item.studentName}`,
        status === "APPROVED" ? "success" : "warning"
      );

      onSuccess();
      onClose();
      setFeedback("");
    } catch (err) {
      console.error(err);
      showToast("Error", "Failed to update resolution status", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSubmission = !!(submission?.evidenceUrl || submission?.contentUrl || submission?.contentBody);
  const showVerifyButton = item?.status !== "PENDING" || (item?.status === "PENDING" && hasSubmission);

  // Calculate status-based border color and premium shadows
  const getStatusStyles = () => {
    if (!item) return "";
    let statusClass = "border-outline-variant/20";
    
    if (item.status === "APPROVED") {
      statusClass = "border-emerald-500";
    } else if (item.status === "REJECTED") {
      statusClass = "border-red-500";
    } else {
      if (hasSubmission) {
        statusClass = "border-blue-500";
      } else {
        statusClass = "border-amber-500";
      }
    }

    return `border border-t-8 ${statusClass}`;
  };

  return (
    <>
      <Dialog 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Task Review"
        size="lg"
        className={getStatusStyles()}
      >
        <div className="space-y-6 sm:space-y-8 py-2">
          {/* Header Info */}
          <div className="flex items-center gap-4 bg-surface-container-low p-4 sm:p-5 rounded-[1.5rem] sm:rounded-[2rem] border border-outline-variant/10">
            <div className="w-12 h-12 rounded-2xl bg-primary text-on-primary flex items-center justify-center font-black text-xl shadow-lg">
              {item?.studentName?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-0.5">
                <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest opacity-40">Reviewing Work</p>
                <div className="w-1 h-1 rounded-full bg-slate-300 hidden sm:block" />
                <span className="text-[10px] font-black text-primary uppercase tracking-widest">Ticket ID #{item?.id?.slice(-6)?.toUpperCase()}</span>
              </div>
              <h4 className="text-base sm:text-lg font-black text-on-surface tracking-tight truncate">{item?.studentName}</h4>
              <p className="text-xs font-bold text-primary truncate">{item?.taskTitle}</p>
            </div>
          </div>

          {/* Task Details Area */}
          {submission && (
            <div className="space-y-3 sm:space-y-4">
              <h5 className="text-[10px] font-black text-on-surface-variant uppercase tracking-[0.2em] px-1 italic">Task Details</h5>
              <div className="bg-surface-container-low border border-outline-variant/10 p-4 sm:p-6 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm space-y-4 sm:space-y-5">
                <div>
                  <h3 className="text-base sm:text-lg font-black text-on-surface tracking-tight leading-tight">{submission.title || item?.taskTitle}</h3>
                  <p className="text-[11px] font-bold text-primary italic mt-1.5 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[12px]">push_pin</span> Progress matters more than perfection.
                  </p>
                </div>
                
                {submission.description && (
                  <div className="space-y-3 pt-4 border-t border-outline-variant/10">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px]">assignment</span> Today's Mission
                    </h4>
                    <div className="text-[13px] sm:text-sm text-slate-500 leading-relaxed font-medium whitespace-pre-wrap max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {submission.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Student Content Area */}
          <div className="space-y-4">
            <h5 className="text-[10px] font-black text-on-surface-variant uppercase tracking-[0.2em] px-1 italic">Student's Work</h5>
            
            {isLoading ? (
              <div className="h-48 bg-surface-container-highest animate-pulse rounded-[1.5rem] sm:rounded-[2.5rem] flex items-center justify-center border border-outline-variant/10">
                <span className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest animate-pulse">Loading Screenshot...</span>
              </div>
            ) : submission && hasSubmission ? (
              <div className="space-y-4">
                {submission.evidenceUrl && (
                  <div className="group relative rounded-[1.5rem] sm:rounded-[2.5rem] overflow-hidden border border-outline-variant/10 shadow-sm bg-black">
                    <img 
                      src={submission.evidenceUrl} 
                      alt="Submission Proof" 
                      className="w-full h-[280px] sm:h-[450px] object-cover transition-transform group-hover:scale-[1.05]"
                      onError={(e) => {
                        (e.target as any).src = "https://images.unsplash.com/photo-1590402444681-cd185427744a?q=80&w=2670&auto=format&fit=crop";
                      }}
                    />
                    <button 
                      onClick={() => {
                        setPreviewUrl(submission.evidenceUrl!);
                        setIsPreviewOpen(true);
                      }}
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white font-black text-[10px] uppercase tracking-widest cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-sm">open_in_new</span>
                      View Full Evidence
                    </button>
                  </div>
                )}

                {submission.contentUrl && (
                  <a 
                    href={submission.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 sm:p-5 bg-primary/5 border border-primary/20 rounded-2xl group hover:bg-primary/10 transition-all"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="material-symbols-outlined text-primary">link</span>
                      <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-widest text-primary/60 mb-0.5">Task Link</p>
                        <p className="text-sm font-bold text-on-surface truncate max-w-[200px] sm:max-w-[400px]">{submission.contentUrl}</p>
                      </div>
                    </div>
                    <span className="material-symbols-outlined text-primary group-hover:translate-x-1 transition-transform flex-shrink-0">arrow_forward</span>
                  </a>
                )}

                {submission.contentBody && (
                  <div className="p-5 sm:p-6 bg-surface-container-highest/30 border border-outline-variant/10 rounded-[1.5rem] sm:rounded-[2rem] overflow-x-auto">
                    <div className="text-sm font-medium text-on-surface leading-relaxed text-left">
                      <ReactMarkdown
                        components={{
                          p: ({node, ...props}) => <p className="mb-4 last:mb-0" {...props} />,
                          h1: ({node, ...props}) => <h1 className="text-xl font-black mb-3 mt-6 text-primary" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-lg font-black mb-3 mt-5 text-primary" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-base font-bold mb-2 mt-4 text-on-surface" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-4 space-y-1" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-4 space-y-1" {...props} />,
                          li: ({node, ...props}) => <li className="pl-1" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-black text-on-surface" {...props} />,
                          a: ({node, ...props}) => <a className="text-primary hover:underline font-bold" target="_blank" rel="noopener noreferrer" {...props} />,
                          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-primary/40 pl-4 py-1 italic bg-primary/5 rounded-r-lg mb-4" {...props} />,
                          code: ({node, ...props}) => <code className="bg-surface-container-highest px-1.5 py-0.5 rounded text-primary text-xs font-mono" {...props} />
                        }}
                      >
                        {submission.contentBody}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-12 sm:py-16 text-center bg-surface-container-high/20 rounded-[1.5rem] sm:rounded-[2.5rem] border-2 border-dashed border-outline-variant/10 space-y-3">
                <span className="material-symbols-outlined text-4xl text-on-surface-variant/20">hourglass_empty</span>
                <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em]">Waiting for student to finish</p>
              </div>
            )}
          </div>

          {/* Coach Action Area */}
          <div className="space-y-4 sm:space-y-5 pt-4 border-t border-outline-variant/10">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest px-1">Note to Student</label>
              <textarea 
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Tell the student what they did well and what needs fixing..."
                className="w-full p-4 sm:p-6 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl sm:rounded-[1.5rem] text-sm font-medium focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                rows={4}
              />
            </div>

            <div className={`grid gap-3 sm:gap-4 ${showVerifyButton ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <button 
                onClick={() => handleResolve("REJECTED")}
                disabled={isSubmitting || !feedback}
                className={`py-4 sm:py-5 rounded-xl sm:rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${
                  !feedback ? "bg-surface-container-high text-on-surface-variant/20 cursor-not-allowed border border-outline-variant/10" : "bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white shadow-xl shadow-red-500/10 border border-red-500/10"
                }`}
              >
                <span className="material-symbols-outlined text-sm">assignment_return</span>
                Needs Revision
              </button>
              {showVerifyButton && (
                <button 
                  onClick={() => handleResolve("APPROVED")}
                  disabled={isSubmitting}
                  className="py-4 sm:py-5 bg-emerald-500 text-white rounded-xl sm:rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] shadow-xl shadow-emerald-500/20 hover:translate-y-[-4px] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <span className="material-symbols-outlined text-sm">verified</span>
                  Approve Task
                </button>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Cinema Mode Evidence Viewer */}
      {mounted && isPreviewOpen && createPortal(
        <div 
          className="fixed inset-0 z-[999999] bg-black flex items-center justify-center animate-in fade-in duration-300 overflow-hidden cursor-zoom-out"
          onClick={() => {
            setIsPreviewOpen(false);
            setZoomScale(1);
          }}
        >
          <button 
            className="absolute top-6 right-6 sm:top-8 sm:right-8 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center backdrop-blur-xl border border-white/10 hover:bg-white hover:text-black transition-all z-[1000000]"
            onClick={(e) => {
              e.stopPropagation();
              setIsPreviewOpen(false);
              setZoomScale(1);
            }}
          >
            <span className="material-symbols-outlined">close</span>
          </button>

          <img 
            src={previewUrl} 
            alt="Evidence Full View" 
            style={{ transform: `scale(${zoomScale})` }}
            className="max-w-full max-h-full object-contain shadow-2xl animate-in zoom-in-95 duration-500 transition-transform cursor-default"
            onClick={(e) => e.stopPropagation()}
          />

          <div 
            className="absolute bottom-6 right-6 sm:bottom-10 sm:right-10 flex items-center gap-2 p-1.5 bg-white/10 backdrop-blur-2xl border border-white/10 rounded-2xl z-[1000000]"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setZoomScale(prev => Math.max(0.2, prev - 0.2))}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all"
              title="Zoom Out"
            >
              <span className="material-symbols-outlined">zoom_out</span>
            </button>
            <div className="w-12 text-center text-[10px] font-black text-white uppercase tracking-tighter">
              {Math.round(zoomScale * 100)}%
            </div>
            <button 
              onClick={() => setZoomScale(prev => Math.min(5, prev + 0.2))}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all"
              title="Zoom In"
            >
              <span className="material-symbols-outlined">zoom_in</span>
            </button>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <button 
              onClick={() => setZoomScale(1)}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all"
              title="Reset Zoom"
            >
              <span className="material-symbols-outlined text-sm">restart_alt</span>
            </button>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <a 
              href={previewUrl} 
              download 
              className="w-9 h-9 rounded-xl bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all shadow-lg"
              title="Download Evidence"
            >
              <span className="material-symbols-outlined text-sm">download</span>
            </a>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
